import { motion } from "framer-motion";
import { Star, Quote, ExternalLink, PenLine } from "lucide-react";

export const GOOGLE_REVIEWS_URL = "https://share.google/67DUWRn34MzPx8YW2";
export const FACEBOOK_URL = "https://facebook.com/1407258252909334";

const ease = [0.16, 1, 0.3, 1];

const reviews = [
  {
    name: "Travis Gonzales",
    source: "Google Review",
    stars: 5,
    text: "Phenomenal job by Scott and crew. Just bought a house which needed three bedrooms and a living room carpet cleaned. These guys worked through a very hot house as the electricity hadn't been turned on yet. They did fantastic with the carpet cleaning. Definitely recommend!",
  },
  {
    name: "Terry Thompson",
    source: "Google Review",
    stars: 5,
    text: "Just had these guys clean my carpets. Very friendly, professional, thorough, and went right to work. And the price was right! Didn't know businesses like this existed anymore. Honest quality service at a reasonable price! Love it!",
  },
  {
    name: "Jim Slaughter",
    source: "Google Review · Property Manager",
    stars: 5,
    text: "I have used Xtra Clean for years to do various types of flooring work and cleaning in the 70+ properties my company manages. Never disappointed in their work. Always use them for personal stuff as well.",
  },
  {
    name: "Alena Creamer",
    source: "Google Review",
    stars: 5,
    text: "Did a fantastic job on my area rug — picked it up and dropped it off at my convenience. Was recommended to me and I would recommend to anyone.",
  },
  {
    name: "Ezekiel Chavarria",
    source: "Google Review",
    stars: 5,
    text: "Honest. Affordable. Dependable. Prompt. Clean.",
  },
];

function Stars({ count, size = 18, className = "" }) {
  return (
    <span className={`inline-flex gap-0.5 ${className}`} aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={size}
          className={i < count ? "fill-red-600 text-red-600" : "text-zinc-300"}
        />
      ))}
    </span>
  );
}

export default function Reviews() {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-zinc-950 text-white py-20 lg:py-32 relative overflow-hidden grain-overlay"
    >
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease }}
            className="lg:col-span-5"
          >
            <p className="font-mono-x text-xs uppercase tracking-[0.3em] text-red-500 font-semibold mb-4">
              What San Angelo Says
            </p>
            <h2 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              RATED BY THE
              <br />
              <span className="text-red-600">NEIGHBORS</span>
              <br />
              WE WORK FOR.
            </h2>

            <div
              data-testid="reviews-google-rating"
              className="mt-10 inline-flex flex-col gap-3 border-2 border-zinc-800 p-6"
            >
              <div className="flex items-end gap-4">
                <span className="font-display font-black text-6xl leading-none">4.5</span>
                <Stars count={5} size={22} className="mb-2" />
              </div>
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-zinc-400">
                Google Rating · 22 Reviews
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                data-testid="reviews-read-all-link"
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white text-zinc-950 hover:bg-red-600 hover:text-white transition-colors duration-200 px-6 py-3.5 font-mono-x text-xs font-semibold uppercase tracking-wider active:scale-[0.98]"
              >
                Read All Google Reviews <ExternalLink size={14} />
              </a>
              <a
                data-testid="reviews-leave-review-link"
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 border-2 border-zinc-700 text-white hover:border-red-600 hover:text-red-500 transition-colors duration-200 px-6 py-3.5 font-mono-x text-xs font-semibold uppercase tracking-wider active:scale-[0.98]"
              >
                Leave a Review <PenLine size={14} />
              </a>
            </div>
          </motion.div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((r, i) => (
              <motion.blockquote
                key={r.name}
                data-testid={`review-card-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.1, ease }}
                className={`relative bg-white text-zinc-900 p-8 lg:p-9 flex flex-col ${
                  i === reviews.length - 1 && reviews.length % 2 === 1
                    ? "md:col-span-2"
                    : ""
                }`}
              >
                <Quote
                  size={44}
                  className="absolute -top-4 right-6 text-red-600 fill-red-600"
                />
                <Stars count={r.stars} size={16} />
                <p className="mt-4 font-display font-semibold text-base sm:text-lg leading-snug tracking-tight flex-1">
                  “{r.text}”
                </p>
                <footer className="mt-6 flex items-center justify-between gap-3 border-t-2 border-zinc-100 pt-4">
                  <div>
                    <p className="font-display font-bold text-sm">{r.name}</p>
                    <p className="font-mono-x text-[10px] uppercase tracking-[0.18em] text-zinc-500 mt-1">
                      {r.source}
                    </p>
                  </div>
                  <span className="font-mono-x text-[10px] uppercase tracking-[0.18em] text-red-600 font-semibold whitespace-nowrap">
                    Verified
                  </span>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
